import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import './OnboardingFlow.css';

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useUser();
  const [name, setName] = useState(state.user?.name || '');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(state.user?.preferredStockTypes || []);
  const [birthYear, setBirthYear] = useState(state.user?.birthYear || new Date().getFullYear() - 25);
  const [birthMonth, setBirthMonth] = useState(state.user?.birthMonth || 1);

  const stockTypes = [
    'Technology',
    'Healthcare',
    'Finance',
    'Energy',
    'Consumer Goods',
    'Real Estate',
    'Utilities',
    'Industrials'
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 18 - i);

  const handleNext = () => {
    if (state.currentStep === 0 && name.trim()) {
      dispatch({ type: 'SET_NAME', payload: name.trim() });
      dispatch({ type: 'NEXT_STEP' });
    } else if (state.currentStep === 1 && selectedTypes.length > 0) {
      dispatch({ type: 'SET_STOCK_TYPES', payload: selectedTypes });
      dispatch({ type: 'NEXT_STEP' });
    } else if (state.currentStep === 2) {
      dispatch({ type: 'SET_BIRTH_DATE', payload: { year: birthYear, month: birthMonth } });
      dispatch({ type: 'COMPLETE_ONBOARDING' });
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    if (state.currentStep > 0) {
      dispatch({ type: 'PREV_STEP' });
    }
  };

  const toggleStockType = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const isStepValid = () => {
    switch (state.currentStep) {
      case 0:
        return name.trim().length > 0;
      case 1:
        return selectedTypes.length > 0;
      case 2:
        return birthYear > 0 && birthMonth > 0;
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (state.currentStep) {
      case 0:
        return "What's your name?";
      case 1:
        return "What industries interest you?";
      case 2:
        return "When were you born?";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    switch (state.currentStep) {
      case 0:
        return "We'll personalize your experience based on your preferences.";
      case 1:
        return "Select the industries you're most interested in investing in.";
      case 2:
        return "This helps us provide age-appropriate investment guidance.";
      default:
        return "";
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((state.currentStep + 1) / 3) * 100}%` }}
            ></div>
          </div>
          <div className="step-indicator">
            Step {state.currentStep + 1} of 3
          </div>
        </div>

        <div className="onboarding-content">
          <h1 className="step-title">{getStepTitle()}</h1>
          <p className="step-subtitle">{getStepSubtitle()}</p>

          <div className="step-content">
            {state.currentStep === 0 && (
              <div className="name-input-container">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="name-input"
                  autoFocus
                />
              </div>
            )}

            {state.currentStep === 1 && (
              <div className="stock-types-grid">
                {stockTypes.map((type) => (
                  <button
                    key={type}
                    className={`stock-type-card ${selectedTypes.includes(type) ? 'selected' : ''}`}
                    onClick={() => toggleStockType(type)}
                  >
                    <span className="stock-type-icon">
                      {type === 'Technology' && '💻'}
                      {type === 'Healthcare' && '🏥'}
                      {type === 'Finance' && '💰'}
                      {type === 'Energy' && '⚡'}
                      {type === 'Consumer Goods' && '🛒'}
                      {type === 'Real Estate' && '🏠'}
                      {type === 'Utilities' && '🔧'}
                      {type === 'Industrials' && '🏭'}
                    </span>
                    <span className="stock-type-name">{type}</span>
                  </button>
                ))}
              </div>
            )}

            {state.currentStep === 2 && (
              <div className="birth-date-container">
                <div className="date-selector">
                  <label>Birth Year</label>
                  <select
                    value={birthYear}
                    onChange={(e) => setBirthYear(parseInt(e.target.value))}
                    className="date-select"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="date-selector">
                  <label>Birth Month</label>
                  <select
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(parseInt(e.target.value))}
                    className="date-select"
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="onboarding-actions">
          {state.currentStep > 0 && (
            <button className="btn-back" onClick={handleBack}>
              Back
            </button>
          )}
          <button 
            className={`btn-next ${!isStepValid() ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={!isStepValid()}
          >
            {state.currentStep === 2 ? 'Complete Setup' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
